import { useState, useEffect } from 'react';
import Calendar from 'react-calendar';
import 'react-calendar/dist/Calendar.css';
import { Link, useLoaderData } from 'react-router';
import { LivestreamGrid } from '#app/components/livestream-grid.tsx';
import SponsorshipsSection from '#app/components/sponsorship-section.tsx';
import WatchCTA from '#app/routes/resources+/watch-cta.tsx'
import { getUserId } from '#app/utils/auth.server.ts';
import { formatDateTimeFromString, formatTimeRangeDuration } from '#app/utils/date.ts';
import { getLivestreamHistory, getLivestreamStats } from '#app/utils/livestream.server.ts';
import { type IScheduleEvent, getWeeklySchedule } from '#app/utils/schedule.server.ts';
import { type Route } from './+types/index.ts';

interface CalendarEvent extends IScheduleEvent {
    isLivestream: boolean;
    youtubeId: string | null;
}

interface CalendarResponse
{
    events: CalendarEvent[];
}


export const meta = ({ location, data }: Route.MetaArgs) => {
	var title = `JaseOwns - Schedule`;
	var description = "You can check out Jase Owns live streams on YouTube and Twitch.";
	return [
		{ title: title },
	  	{ name: "description", content: description},
	  	{ name: "keywords", content: "schedule,creator,videos,uo,ultima online,self taught,learning,jaseowns,jase,jason"},
		{ property: "og:image", content: "https://jaseowns.com/images/schedule.png"},
		{ property: "og:image:alt", content: "Schedule for JaseOwns"},
		{ property: "twitter:card", content: "summary_large_image"},
		{ property: "twitter:creator", content: "@jaseowns"},
		{ property: "twitter:site", content: "@jaseowns"},
		{ property: "og:title", content: title},
		{ property: "og:description", content: description},
	];
  };

// type TimeSlot = {
//     start?: Date | null;
//     end?: Date | null;
//     time: string;
//     color: string;
//     content: JSX.Element | string;
// }

export async function loader({ request }: Route.LoaderArgs) {
    const user = await getUserId(request);
	//await requireAnonymous(request)
	// const session = await getSession(request.headers.get('cookie'))
	// const error = session.get(authenticator.sessionErrorKey)
	// const onboardingEmail = session.get(onboardingEmailSessionKey)
	// if (typeof onboardingEmail !== 'string' || !onboardingEmail) {
	// 	return redirect('/signup')
	// }
    //const now = new Date();
    // var fullSchedule = await getAllSchedule(now);
    // var pastEvents = fullSchedule.filter(x => x.utcDateString <= new Date().toISOString());
    // var futureEvents = fullSchedule.filter(x => x.utcDateString >= new Date().toISOString());
    // var currentEvents = fullSchedule.filter(x => x.utcDateString <= new Date().toISOString() && x.utcDateString >= new Date().toISOString());

    const weeklyEvents = await getWeeklySchedule();
    const livestreams = await getLivestreamHistory({ limit: 6 });
    const livestreamStats = await getLivestreamStats();

	return { user, formError: null, weeklyEvents, livestreams, livestreamStats }
}




const scheduleUpdates = [
  {
    date: '03/23/2026',
    content: 'We are back to our regular scheduled 6-9 PM CST every Monday, Wednesday, Friday. Live on Twitch and YouTube. Calendar now shows past streams!',
  },
  {
    date: '02/11/2026',
    content: "I'm going dark for a few weeks starting today (2/11). Streams will pause until March 9th, when we return to our regular M/W/F 6-9 PM CST schedule. This is a strategic reset - building, planning, upgrading. When we're back, we're back strong! Appreciate you all.",
  },
  {
    date: '08/19/2025',
    content: "It's already middle of August! We've been on the 6-9 PM CST schedule for a while now. I still need to add the past streams to the calendar, but I'm working on it.",
  },
  {
    date: '04/27/2025',
    content: 'Life has been a little hectic, but we are still on Monday, Wednesday, Friday for a stream schedule. My side quest is to make sure all my live streams also show up here as we go live. I also want to fill in past streams to show consistency.',
    links: true,
  },
  {
    date: '01/01/2025',
    content: '2025 will be our year. Monday, Wednesday, Friday will be live stream days. Every Wednesday we will publish a new video. Check out the Discord for more info.',
  },
  {
    date: '12/18/2024',
    content: "I'm back from a break during some company visits and my daughter being in the hospital. We should be back on the full-time streamer grind soon heading into 2025!",
  },
  {
    date: '12/01/2024',
    content: "We're in the last month of the year. We are a month into trying to work for ourselves and embrace full time streamer status. I have more company coming to town starting 12/10 however, we will be streaming 32 hours on the weekdays and 8 on the weekends.",
  },
  {
    date: '11/24/2024',
    content: 'We made it three weeks into the schedule. We have company in town now, but a new schedule will be announced December 1st.',
  },
  {
    date: '11/4/2024',
    content: 'I plan on streaming 3 times a day during the weekdays, 6-9am, 11am-2pm, and 6-9pm CST on YouTube and Twitch. We will focus on creating UO Content and building our personal brand for the future. We will try this schedule for three weeks and revaluate. I hope to see you there!',
  },
  {
    date: '06/10/2022',
    content: 'Starting my streaming journey on YouTube.',
    linkHref: 'https://youtube.com/live/zgNxnJWx_UQ?feature=share',
    linkText: 'Teaching UO Outlands Razor Scripts LIVE - Thief Edition',
  },
];

export default function ScheduleCalendar() {
   const { user, weeklyEvents, livestreams, livestreamStats } = useLoaderData<typeof loader>()
   const latestUpdate = scheduleUpdates[0]!;
   const previousUpdates = scheduleUpdates.slice(1);

  const [selectedDate, setSelectedDate] = useState(new Date());
  const [events, setEvents] = useState<CalendarEvent[]>([]);
  const [todaysEvents, setTodaysEvents] = useState<CalendarEvent[]>([]);

  const handleDateChange = (value: Date) => {
      setSelectedDate(value);
  };

  // Check if an event falls on a given local date
  const eventOnLocalDate = (event: CalendarEvent, date: Date) => {
    const eventStart = new Date(event.utcDateString);

    if (event.isLivestream) {
      // Livestream publishedAt times are inaccurate (VOD publish time, not go-live time)
      // Just match on the local date the timestamp falls on
      return eventStart.toDateString() === date.toDateString();
    }

    // For scheduled events, check if the event's time range overlaps this day
    const duration = event.estimatedDuration || 180;
    const eventEnd = new Date(eventStart.getTime() + duration * 60000);
    const dayStart = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    const dayEnd = new Date(date.getFullYear(), date.getMonth(), date.getDate() + 1);
    return eventStart < dayEnd && eventEnd > dayStart;
  };

  useEffect(() => {
    async function getData() {
        const response = await fetch(`/api/calendar?date=${selectedDate.toISOString()}`);
        const data = (await response.json()) as CalendarResponse;
        setEvents(data.events);
        setTodaysEvents(data.events.filter((event) => eventOnLocalDate(event, selectedDate)));
    }
    void getData();
  }, [selectedDate]);


  const gameOfTheMonth = {
    title: "Game of the Month",
    image: "/images/games/mount-and-blade-ii-bannerlord.jpg",
    alt: "Mount and Blade II: Bannerlord Logo",
}
const bannerLordGame = {
    title: "Game of the Month",
    image: "/images/games/mount-and-blade-ii-bannerlord.jpg",
    alt: "Mount and Blade II: Bannerlord Logo",
}
const ultimaOnlineGame = {
    title: "Ultima Online",
    image: "/images/games/uo-logo.jpg",
    alt: "Ultima Online Logo",
}
const productivity = {
    title: "Focus Session",
    image: "/images/games/jaseowns-focus-session.png",
    alt: "Productivity & Focus",
}
const workdaySession = {
    title: "Building Future",
    image: "/images/games/jaseowns-workday-session.png",
    alt: "Building for the future",
}

const getImageFromType = (type: string) => {
    switch(type) {
        case "Bannerlord":
            return bannerLordGame
        case "Game of the Month":
            return gameOfTheMonth
        case "Ultima Online":
            return ultimaOnlineGame
        case "Focus Session":
            return productivity
        case "Building Future":
        case "Workday":
            return workdaySession
        default:
            return ultimaOnlineGame
    }
}



  return (
		<main className="container mx-auto text-black dark:text-white">
        <h1 className="text-3xl px-10 mb-6 text-cyan-900 dark:text-cyan-200 flex flex-row items-center gap-2">
        <img src="/images/twitch.png" alt="Twitch" className="w-8 h-8" /> Streaming Schedule <img src="/images/youtube.png" alt="YouTube" className="w-10 h-10" />
      </h1>
      <div className="flex flex-col px-10 mb-6">
            <p className='py-2'>
                <b className='text-xl'>UPDATE {latestUpdate.date}:</b> {latestUpdate.content}
            </p>
            <button type="button" onClick={() => document.getElementById('schedule-history')?.scrollIntoView({ behavior: 'smooth' })} className="text-sm text-cyan-600 dark:text-cyan-400 hover:underline text-left w-fit">Previous updates &darr;</button>

            <h2 className="text-2xl py-4 text-cyan-900 dark:text-cyan-200">
                This weeks events:
            </h2>
            
            {weeklyEvents?.map((day: IScheduleEvent) => {
                return <div key={`weekly_${day.id}`}>
                        {formatDateTimeFromString(day.utcDateString)} {day.title} - {day.description}
                    </div>
            })}
        </div>
      <div className="flex flex-col md:flex-row px-10 mb-6">
        <div className='p-2 md:p-8'>
              {/* @ts-ignore*/}
              <Calendar calendarType='gregory' onChange={handleDateChange} value={selectedDate}
                tileClassName={({ date, view }: { date: Date, view: string }) =>
                  view === "month" && events.some(event => eventOnLocalDate(event, date))
                    ? "highlight"
                    : null
                }
              />

            { user ? 
            <Link to="/schedule/add" type="button" className="text-center mt-8 w-full rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600">
                Add event
            </Link> : <></> }
        </div>
        <div className='p-2 md:p-8'>
          <h2>Events on {selectedDate.toDateString()}:</h2>
          
          <div className=' flex flex-col md:flex-row'>
            {todaysEvents.length ? todaysEvents.map((event) => {
              if (event.isLivestream) {
                return <a href={`https://www.youtube.com/watch?v=${event.youtubeId}`} target="_blank" key={event.id} rel="noreferrer" className="p-2">
                    <div className="p-2 md:p-8 text-center">
                        <div className="text-xs uppercase tracking-wide text-red-500 font-semibold mb-1">Past Livestream</div>
                        <div>
                            {event.title}<br />
                            <img className="m-auto max-w-[320px] rounded" src={`https://img.youtube.com/vi/${event.youtubeId}/mqdefault.jpg`} alt={event.title} />
                        </div>
                    </div>
                  </a>
              }

              const typeHelper = getImageFromType(event.type)
              return <a href="https://www.youtube.com/jaseowns/live" key={event.id} rel="noreferrer" className="p-2">
                  <div className={`p-2 md:p-8 text-center`}>
                      {formatTimeRangeDuration(event.utcDateString, event.estimatedDuration)}
                      <div>
                          {event.title}<br />
                          <img className="m-auto" src={typeHelper.image} alt={typeHelper.alt} />
                      </div>
                      <div className='hidden'>{event.description}</div>
                  </div>
                </a>


            }) : <span>No events</span>}
          </div>
        </div>
      </div>

      <h1 className="text-3xl px-10 mb-6 text-cyan-900 dark:text-cyan-200">
          Past Livestreams
      </h1>
      <div className="flex flex-col px-10 mb-6">
        <LivestreamGrid
          livestreams={livestreams}
          livestreamStats={livestreamStats}
          searchQuery=""
        />
        <Link to="/livestreams" className="mt-4 text-cyan-600 dark:text-cyan-400 hover:underline font-medium">View all livestreams &rarr;</Link>
      </div>

      <h1 id="schedule-history" className="text-3xl px-10 mb-6 text-cyan-900 dark:text-cyan-200">
          Schedule History
      </h1>
      <div className="flex flex-col px-10 mb-6">
        {previousUpdates.map((update) => (
          <p className='py-2' key={update.date}>
            <b>{update.date}:</b> {update.content}
            {update.linkHref && (
              <> Check out the first stream here: <a href={update.linkHref} className='underline'>{update.linkText}</a></>
            )}
            {update.links && (
              <> Every Wednesday a new <Link to="/videos" className='underline'>YouTube video</Link> drops. Be sure to check out the <a href="https://discord.gg/jaseowns" className='underline'>Discord</a> for more info and chat with the community.</>
            )}
          </p>
        ))}
      </div>

      <div className="flex flex-col px-10 mb-6">
        <SponsorshipsSection />
      </div>

      <div className="flex flex-wrap justify-center gap-8 rounded-3xl  py-4">
            {WatchCTA()}
        </div>


    </main>
  );
}

